import Divider from 'components/Divider';
import { observer } from 'mobx-react-lite';

import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'hooks/useModal';
import MissingServicesModal from '../../Locations/MissingServicesModal';
import { useAuthUser } from 'hooks/useAuthUser';
import { toJS } from 'mobx';
import { postQuote, updateOrder } from 'api/accessexpressv3/api';
import axios from 'axios';
import { postQuoteToRequest } from 'screens/AccessExpressV3/AccessProductDetails/utils';
import ClearAllModal from '../../ClearAllModal';

const LocationFooter = observer(() => {
  const cart = useCartContext();
  const navigate = useNavigate();
  const userInfo = useAuthUser();

  const handleCheckout = () => {
    if (cart.locationMissingServices.length) missingServicesModalOpen();
    else onProceedToCheckout();
  };

  const { open: missingServicesModalOpen, ...missingServicesModalProps } =
    useModal();

  const onProceedToCheckout = async () => {
    cart.removeLocationsMissingServices();
    const dataForPostOrUpdate = {
      additional_details: cart.additionalDetails ?? {},
      locations: cart.locations.map((location) => {
        const order_items = cart
          .itemsByLocation(location.id)
          .filter((item) => item.category === 'Access')
          .map((item) => ({
            type: item.category ?? '',
            subtype: item.subCategory ?? '',
            // need to handle
            pim_price_id: null,
            item_details: toJS(item.item_details) ?? [],
            price: item.price ?? 0,
            quantity: item.quantity ?? 0,
            location_id: String(item.locationId ?? ''),
          }));
        return {
          address: {
            ...location,
            id: location.id as string,
          },
          order_items,
        };
      }),
    };

    try {
      const draft = await updateOrder(dataForPostOrUpdate, cart.draftId!);
      cart.updateStateFromDraft(draft);
    } catch (error) {
      console.error(error);
    }
    navigate('checkout/access');
  };
  const handleClearAll = () => {
    cart.clearAllAccessItems();
  };

  const syncCartWithBackend = async () => {
    if (cart.draftId) {
      cart.setLoadingDraftQuote(true);
      // NOTE: This logic is duplicated across multiple components (handleLocationsChange, handleFindServices).
      const dataForPostOrUpdate = {
        additional_details: cart.additionalDetails ?? {},
        locations: cart.locations.map((location) => {
          const order_items = cart
            .itemsByLocation(location.id)
            .filter((item) => item.category === 'Access')
            .map((item) => ({
              type: item.category ?? '',
              subtype: item.subCategory ?? '',
              // need to handle
              pim_price_id: null,
              item_details: toJS(item.item_details) ?? [],
              price: item.price ?? 0,
              quantity: item.quantity ?? 0,
              location_id: String(item.locationId ?? ''),
            }));
          return {
            address: {
              ...location,
              id: location.id as string,
            },
            order_items,
          };
        }),
      };
      const draft = await updateOrder(dataForPostOrUpdate, cart.draftId);
      cart.updateStateFromDraft(draft);
      await postQuote(
        postQuoteToRequest(userInfo, cart.locations, [
          {
            product_type: cart.selectedAccessProductCategory as
              | 'Broadband'
              | 'DIA',
            ...cart.additionalDetails?.configs,
          },
        ]),
      )
        .then(async (quoteResponse) => {
          const additionalDetails = {
            configs: cart.additionalDetails?.configs,
            ...(cart.selectedAccessProductCategory === 'DIA'
              ? { dia_quote_request: quoteResponse.id }
              : { bb_quote_request: quoteResponse.id }),
          };
          await updateOrder(
            {
              additional_details: additionalDetails,
            },
            draft?.id ?? '',
          )
            .then((data) => cart.updateStateFromDraft(data))
            .finally(() => {
              cart.setLoadingDraftQuote(false);
            });
        })
        .catch((error) => {
          if (axios?.isAxiosError(error) && error?.name === 'CanceledError')
            return;
          cart.setLoadingDraftQuote(false);
          console.log('Error creating quote:', error);
        });
    }
  };

  const handleDone = () => {
    cart.doneEditingLocations();
    syncCartWithBackend();
  };

  const handleCancel = () => {
    cart.cancelEditingLocations();
  };

  return (
    <div className="flex flex-col gap-8">
      {cart.isEditingLocations ? (
        <div className="flex gap-4">
          <GraniteButton
            variant="secondary"
            className="w-full"
            onClick={handleCancel}
          >
            Cancel
          </GraniteButton>
          <GraniteButton
            variant="tertiary"
            className="w-full"
            onClick={handleDone}
          >
            Done
          </GraniteButton>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <Divider />
            <div className="flex justify-between">
              <GraniteButton
                size="small"
                variant="ghost"
                className="hover:!border-accent-primary hover:!text-accent-primary"
                onClick={() => cart.editLocations()}
              >
                Edit locations
              </GraniteButton>
              <ClearAllModal
                onClick={handleClearAll}
                disabled={cart.items.length === 0}
              />
            </div>
          </div>

          {cart.totalItems !== 0 && (
            <GraniteButton
              variant="tertiary"
              onClick={handleCheckout}
              disabled={cart.loadingDraftForPricing || cart.loadingDraftQuote}
            >
              Checkout ({cart.totalItems})
            </GraniteButton>
          )}
          <MissingServicesModal
            {...missingServicesModalProps}
            onProceedToCheckout={onProceedToCheckout}
          />
        </>
      )}
    </div>
  );
});

export default LocationFooter;
